import React from 'react';
import { Alert, MenuItem, TextField } from '@mui/material';
import { type AutoIssueState } from '../autoIssue';
import type { IssueTrigger } from '../types';

const TRIGGERS: { value: IssueTrigger; label: string; hint: string }[] = [
  { value: 'NONE', label: 'None (create codes manually)', hint: '' },
  {
    value: 'REGISTER',
    label: 'When an account is activated',
    hint: 'Welcome gift. Email accounts get it after verifying their email.',
  },
  {
    value: 'REFERRED_REGISTER',
    label: 'Friend enters a referral code (reward for the friend)',
    hint: 'Issued to the new user when they apply a friend’s referral code.',
  },
  {
    value: 'REFERRER_ON_REGISTER',
    label: 'Friend enters my referral code (reward for the referrer)',
    hint: 'Issued to the referrer immediately, so cap it per month to limit abuse.',
  },
  {
    value: 'REFERRER_ON_FIRST_PAYMENT',
    label: 'Friend makes their first payment (reward for the referrer)',
    hint: 'Recommended referral reward: it only pays out after a real purchase.',
  },
  {
    value: 'FIRST_PAYMENT',
    label: 'User makes their first payment',
    hint: 'Thank-you code for the next purchase.',
  },
];

const digits = (value: string) => value.replace(/[^\d]/g, '');

interface Props {
  value: AutoIssueState;
  onChange: (next: AutoIssueState) => void;
}

export const AutoIssueSection: React.FC<Props> = ({ value, onChange }) => {
  const set = <K extends keyof AutoIssueState>(key: K, v: AutoIssueState[K]) =>
    onChange({ ...value, [key]: v });
  const active = TRIGGERS.find((t) => t.value === value.trigger);
  const isReferrer = value.trigger.startsWith('REFERRER_');

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-slate-700">
      <TextField
        select
        label="Auto-issue codes"
        size="small"
        fullWidth
        value={value.trigger}
        onChange={(e) => set('trigger', e.target.value as IssueTrigger)}
        helperText={active?.hint || 'The system does not issue codes for this campaign.'}
      >
        {TRIGGERS.map((t) => (
          <MenuItem key={t.value} value={t.value}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>

      {value.trigger !== 'NONE' && (
        <>
          <Alert severity="info">
            Each recipient gets a personal single-use code, sent as a notification (and email unless
            they opted out). Only accounts created after the campaign starts are eligible.
          </Alert>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="Code prefix"
              size="small"
              value={value.prefix}
              onChange={(e) =>
                set('prefix', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))
              }
              helperText="e.g. WELCOME → WELCOME-7K3QX92MAB"
            />
            <TextField
              label="Valid for (days)"
              size="small"
              value={value.expiresInDays}
              onChange={(e) => set('expiresInDays', digits(e.target.value))}
              helperText="Counted from the day it is issued (1–365)"
            />
            <TextField
              label="Codes per recipient"
              size="small"
              value={value.limitPerUser}
              onChange={(e) => set('limitPerUser', digits(e.target.value))}
            />
            {isReferrer && (
              <TextField
                label="Max rewards per referrer / 30 days"
                size="small"
                value={value.maxPerReferrer}
                onChange={(e) => set('maxPerReferrer', digits(e.target.value))}
                helperText="Strongly recommended to limit abuse"
              />
            )}
          </div>

          <p className="text-xs text-gray-500 dark:text-slate-400">
            Notification text (optional). Variables: {'{{code}} {{discount}} {{expiresAt}}'}. Empty
            fields use the default message.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <TextField
                label="Title (vi)"
                size="small"
                fullWidth
                value={value.viTitle}
                onChange={(e) => set('viTitle', e.target.value)}
              />
              <TextField
                label="Body (vi, markdown)"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={value.viBody}
                onChange={(e) => set('viBody', e.target.value)}
              />
              <TextField
                label="Button (vi)"
                size="small"
                fullWidth
                value={value.viCta}
                onChange={(e) => set('viCta', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <TextField
                label="Title (en)"
                size="small"
                fullWidth
                value={value.enTitle}
                onChange={(e) => set('enTitle', e.target.value)}
              />
              <TextField
                label="Body (en, markdown)"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={value.enBody}
                onChange={(e) => set('enBody', e.target.value)}
              />
              <TextField
                label="Button (en)"
                size="small"
                fullWidth
                value={value.enCta}
                onChange={(e) => set('enCta', e.target.value)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
